import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { dispatchPaymentDetailsIframeEvent } from '../../../../helpers/events'
import { OMNIO_ERROR_TEXT } from '../../../../helpers/omnioBalanceErrors'
import { useTranslations } from '../../../../hooks/useTranslations'
import { pageStateSelector } from '../../../../redux/slice/pageStateSlice'
import { prePaymentSelector } from '../../../../redux/slice/prePaymentSlice'
import ErrorMessage from '../../../Common/ErrorMessage'
import { ERROR_RESPONSES } from '../../../../constants/serverResponses'
import { RESPONSE_ERROR_TEXT } from '../../../../helpers/serverTextResponses'
import {
  CARDINAL_LIB_STATUSES,
  CARDINAL_STATUSES,
} from '../../../../constants/cardinal'
import {
  APEXX_RESPONSE_ERROR_TEXT,
  ERRORS_TEXT,
} from '../../../../helpers/apexxTextResponses'
import { cardinalCruiseSelector } from '../../../../redux/slice/cardinalCruiseSlice'
import { paymentResponseSelector } from '../../../../redux/slice/paymentResponseSlice'

const CardErrors = ({
  hasBeenTouchedAfterSubmit = false,
  paymentBlockedError = '',
  cardNotAcceptedError = '',
  paymentErrorCode = '',
  isCardError = false,
  errorFields = [],
  iframeRef,
}) => {
  const l10n = useTranslations()
  const paymentResponse = useSelector(paymentResponseSelector)
  const cardinalCruise = useSelector(cardinalCruiseSelector)
  const prePayment = useSelector(prePaymentSelector)
  const pageState = useSelector(pageStateSelector)
  const { isPaymentPending, hasError, isRetryable, errorCode } = paymentResponse
  const { cardinalStatus, libStatus } = cardinalCruise

  const hasPaymentErrorAfterSubmit =
    !hasBeenTouchedAfterSubmit && !isPaymentPending && hasError && isRetryable

  const hasUserDetailsError =
    cardinalStatus === CARDINAL_STATUSES.error && !hasBeenTouchedAfterSubmit

  const scrollToErrorField = errorField => {
    // Scroll parent viewport to the iframe position where the error field is.
    // We can't reliably use postMessage + scrollIntoView in Safari
    // (cross-origin iframe + shadow DOM prevents scroll propagation).
    // Instead, scroll the iframe into view in the parent frame.
    if (iframeRef?.current) {
      const iframeRect = iframeRef.current.getBoundingClientRect()
      // Scroll to the top third of the iframe as a best approximation
      // for where form fields are located
      const topOffset = 500
      const targetY = window.scrollY + topOffset + iframeRect.top - window.innerHeight / 3
      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      })
    }

    // Also send message to iframe for input focus (works when postMessage is delivered)
    dispatchPaymentDetailsIframeEvent(
      {
        type: 'MFEScrollTo',
        data: { elementId: `label-${errorField}` },
      },
      iframeRef?.current?.contentWindow,
    )
  }

  return (
    <>
      <ErrorMessage
        isShown={Boolean(errorFields.length)}
        data-testid="payment-error"
        message={l10n.t('FIELDS_ERROR')}
      >
        <label
          htmlFor={errorFields[0]}
          className="button medium error payment-error__anchor"
          tabIndex="0"
          data-testid="payment-error-label"
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.target.click()
            }
          }}
          onClick={() => scrollToErrorField(errorFields[0])}
        >
          {l10n.t('Show me')}
        </label>
      </ErrorMessage>

      <ErrorMessage
        isShown={libStatus === CARDINAL_LIB_STATUSES.failed}
        message={RESPONSE_ERROR_TEXT(l10n, ERROR_RESPONSES.SERVER_ERROR)}
      />

      <ErrorMessage
        data-testid="payment-cardinal-error"
        isShown={cardinalStatus === CARDINAL_STATUSES.connectivity}
        message={l10n.t(ERRORS_TEXT.PLEASE_TRY_AGAIN)}
      />

      <ErrorMessage
        isShown={cardinalStatus === CARDINAL_STATUSES.timeout}
        message={l10n.t('TIMEOUT_REQUEST')}
      />

      <ErrorMessage
        data-testid="payment-error-user-details"
        isShown={hasUserDetailsError}
        message={RESPONSE_ERROR_TEXT(l10n, ERROR_RESPONSES.USER_DETAILS_ERROR)}
      />

      <ErrorMessage
        data-testid="omnio-balance-error"
        isShown={prePayment?.hasError}
        message={OMNIO_ERROR_TEXT(l10n, prePayment.errorCode)}
      />

      {pageState.isCardNumberOmnio && (
        <ErrorMessage
          data-testid="omnio-payment-error-after-submit"
          isShown={!!paymentErrorCode}
          message={OMNIO_ERROR_TEXT(l10n, paymentErrorCode)}
        />
      )}

      {hasPaymentErrorAfterSubmit && !pageState.isCardNumberOmnio && (
        <ErrorMessage
          data-testid="payment-error-after-submit"
          isShown={hasPaymentErrorAfterSubmit}
          message={APEXX_RESPONSE_ERROR_TEXT(l10n, errorCode)}
        />
      )}

      <ErrorMessage
        isShown={isCardError}
        message={l10n.t('Please select card before payment')}
      />

      <ErrorMessage
        isShown={Boolean(paymentBlockedError)}
        message={paymentBlockedError}
      />

      <ErrorMessage
        data-testid="card-not-accepted-error"
        isShown={Boolean(cardNotAcceptedError)}
        message={cardNotAcceptedError}
      />
    </>
  )
}

CardErrors.propTypes = {
  hasBeenTouchedAfterSubmit: PropTypes.bool,
  paymentBlockedError: PropTypes.string,
  cardNotAcceptedError: PropTypes.string,
  paymentErrorCode: PropTypes.string,
  isCardError: PropTypes.bool,
  errorFields: PropTypes.array,
  iframeRef: PropTypes.object,
}

export default CardErrors
